import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader } from '../../../components'
import { FormattedRelativeTime } from '../../../components/FormattedRelativeTime'
import { fetchCoverageEventDifferences } from '../../../services/coverageEventServices'
import { coverageEventQueryKeys } from './coverageEventQueryKeys'
import { foundStockDeepLink } from '../../FoundStock/foundStockLinks'
import { Pill } from './Pill'

const T = 'cycle_counts.events.differences'
const cell = 'px-3 py-1.5 text-sm text-gray-900'

// The platform sends the SKU/name on each row already — no per-product lookup needed here.
const productLabel = row => `${row.product_sku ?? '—'} · ${row.product_name ?? ''}`

const OriginBadge = ({ owned, i18n }) => (
  <Pill className={owned ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}>{i18n.t(owned ? `${T}.owned` : `${T}.watched`)}</Pill>
)

export const CoverageEventDifferences = ({ eventId, warehouseId }) => {
  const { i18n } = useTranslation()
  const { data, isLoading, isError } = useQuery(coverageEventQueryKeys.differences(eventId), () => fetchCoverageEventDifferences(eventId), {
    refetchOnWindowFocus: false,
  })

  const shortages = data?.shortages ?? []
  const surpluses = data?.surpluses ?? []
  const totals = data?.totals ?? {}

  if (isLoading) return <Loader show />
  if (isError) return <p className="text-sm text-red-700">{i18n.t('cycle_counts.events.errors.UNEXPECTED')}</p>
  if (shortages.length === 0 && surpluses.length === 0) return <p className="text-sm text-gray-500">{i18n.t(`${T}.none`)}</p>

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {i18n.t(`${T}.totals`, {
          // The copy's placeholder names don't match the platform's field names one-to-one —
          // map explicitly rather than assume they line up (a `?? 0` fallback on a typo'd key
          // silently renders zeros against the real API).
          counted: totals.locations_counted ?? 0,
          with_differences: totals.locations_with_differences ?? 0,
          short_owned: totals.units_short_owned ?? 0,
          short_watched: totals.units_short_watched ?? 0,
          surplus_owned: totals.units_surplus_observed_owned ?? 0,
          surplus_watched: totals.units_surplus_observed_watched ?? 0,
          declared: totals.units_surplus_declared ?? 0,
          settled: totals.units_surplus_settled ?? 0,
        })}
      </p>
      <section>
        <h4 className="text-sm font-medium text-gray-900 mb-1">{i18n.t(`${T}.shortages`)}</h4>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              {['origin', 'location', 'product', 'lot', 'system', 'counted', 'difference'].map(c => (
                <th key={c} className="px-3 py-1 text-left text-xs text-gray-500">
                  {i18n.t(`${T}.columns.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shortages.map(r => (
              <tr key={`${r.cycle_count_id}-${r.product_id}`}>
                <td className={cell}>
                  <OriginBadge owned={r.owned} i18n={i18n} />
                </td>
                <td className={`${cell} font-mono`}>{r.location_code}</td>
                <td className={cell}>{productLabel(r)}</td>
                <td className={cell}>{r.lot_id ?? '-'}</td>
                <td className={cell}>{r.system_quantity}</td>
                <td className={cell}>{r.counted_quantity}</td>
                <td className={`${cell} text-red-700`}>{r.difference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section>
        <h4 className="text-sm font-medium text-gray-900 mb-1">{i18n.t(`${T}.surpluses`)}</h4>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              {['origin', 'location', 'product', 'lot', 'declared', 'settlement', 'settled_at'].map(c => (
                <th key={c} className="px-3 py-1 text-left text-xs text-gray-500">
                  {i18n.t(`${T}.columns.${c}`)}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {surpluses.map(r => {
              const key = r.declaration?.id ?? `${r.warehouse_location_id}-${r.product_id}`
              return (
                <tr key={key}>
                  <td className={cell}>
                    <OriginBadge owned={r.owned} i18n={i18n} />
                  </td>
                  <td className={`${cell} font-mono`}>{r.location_code}</td>
                  <td className={cell}>{productLabel(r)}</td>
                  <td className={cell}>{r.lot_id ?? '-'}</td>
                  <td className={cell}>
                    {r.declaration ? `${r.declaration.effective_quantity}${r.declaration.corrected ? ` (${i18n.t(`${T}.corrected`)})` : ''}` : '—'}
                  </td>
                  <td className={cell}>{r.declaration ? i18n.t(`${T}.settlement.${r.declaration.settlement}`) : i18n.t(`${T}.no_declaration`)}</td>
                  <td className={cell}>{r.declaration?.settled_at ? <FormattedRelativeTime date={r.declaration.settled_at} /> : '-'}</td>
                  <td className={cell}>
                    {r.declaration && (
                      <Link
                        className="text-xs text-indigo-700 underline"
                        to={foundStockDeepLink(warehouseId, { location_code: r.location_code, declaration_id: r.declaration.id })}
                      >
                        {i18n.t(`${T}.view_in_found_stock`)}
                      </Link>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}
